import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { UserCircleIcon, KeyIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../components/Spinner';

export const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    setValue: setProfileValue,
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  // Ensure email is always registered so it's included in form data
  useEffect(() => {
    if (user?.email) setProfileValue('email', user.email);
  }, [user?.email, setProfileValue]);

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch,
  } = useForm();

  const newPassword = watch('newPassword');

  const onProfileUpdate = async (data) => {
    setIsUpdating(true);
    await updateProfile(data);
    setIsUpdating(false);
  };

  const onPasswordChange = async (data) => {
    setIsChangingPassword(true);
    await changePassword(data);
    resetPassword();
    setIsChangingPassword(false);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin': return 'badge-danger';
      case 'admin': return 'badge-warning';
      case 'dealer': return 'badge-primary';
      case 'sales_executive': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'dealer': return 'Dealer';
      case 'sales_executive': return 'Sales Executive';
      default: return role;
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserCircleIcon },
    { id: 'security', name: 'Security', icon: KeyIcon },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
      </div>

      <div className="bg-white shadow rounded-lg">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-6 text-center text-sm font-medium hover:bg-gray-50 focus:z-10 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="flex items-center justify-center">
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="max-w-2xl">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Update your personal details and contact information.
                </p>
              </div>

              <form onSubmit={handleProfileSubmit(onProfileUpdate)} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="label">First Name</label>
                    <input
                      {...registerProfile('firstName', {
                        required: 'First name is required',
                      })}
                      type="text"
                      className={`input ${profileErrors.firstName ? 'input-error' : ''}`}
                    />
                    {profileErrors.firstName && (
                      <p className="mt-1 text-sm text-danger-600">{profileErrors.firstName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Last Name <span className="text-xs font-normal text-gray-400">(optional)</span></label>
                    <input
                      {...registerProfile('lastName')}
                      type="text"
                      className={`input ${profileErrors.lastName ? 'input-error' : ''}`}
                    />
                    {profileErrors.lastName && (
                      <p className="mt-1 text-sm text-danger-600">{profileErrors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...registerProfile('email', {
                        required: 'Email is required',
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' },
                      })}
                      type="email"
                      disabled={!isSuperAdmin}
                      className={`input pl-10 ${!isSuperAdmin ? 'bg-gray-50' : ''} ${profileErrors.email ? 'input-error' : ''}`}
                    />
                  </div>
                  {profileErrors.email && (
                    <p className="mt-1 text-sm text-danger-600">{profileErrors.email.message}</p>
                  )}
                  {!isSuperAdmin && (
                    <p className="mt-1 text-sm text-gray-500">
                      Email cannot be changed. Contact administrator if needed.
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      {...registerProfile('phone')}
                      type="tel"
                      className={`input pl-10 ${profileErrors.phone ? 'input-error' : ''}`}
                    />
                  </div>
                  {profileErrors.phone && (
                    <p className="mt-1 text-sm text-danger-600">{profileErrors.phone.message}</p>
                  )}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Updating...
                      </>
                    ) : (
                      'Update Profile'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="max-w-2xl">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Ensure your account is using a strong password.
                </p>
              </div>

              <form onSubmit={handlePasswordSubmit(onPasswordChange)} className="space-y-6">
                <div>
                  <label className="label">Current Password</label>
                  <input
                    {...registerPassword('currentPassword', {
                      required: 'Current password is required',
                    })}
                    type="password"
                    className={`input ${passwordErrors.currentPassword ? 'input-error' : ''}`}
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-danger-600">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">New Password</label>
                  <input
                    {...registerPassword('newPassword', {
                      required: 'New password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters long',
                      },
                    })}
                    type="password"
                    className={`input ${passwordErrors.newPassword ? 'input-error' : ''}`}
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-danger-600">{passwordErrors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Confirm New Password</label>
                  <input
                    {...registerPassword('confirmPassword', {
                      required: 'Please confirm your new password',
                      validate: (value) => value === newPassword || 'Passwords do not match',
                    })}
                    type="password"
                    className={`input ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-danger-600">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChangingPassword ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Changing Password...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Account Information Sidebar */}
        <div className="border-t border-gray-200 p-6">
          <div className="max-w-2xl">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Role</p>
                  <p className="text-sm text-gray-500">Your current role in the system</p>
                </div>
                <span className={`badge ${getRoleBadge(user?.role)}`}>
                  {getRoleDisplayName(user?.role)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Account Status</p>
                  <p className="text-sm text-gray-500">Your current account status</p>
                </div>
                <span className={`badge ${user?.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                  {user?.status || 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Member Since</p>
                  <p className="text-sm text-gray-500">When you joined the platform</p>
                </div>
                <span className="text-sm text-gray-900">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Last Login</p>
                  <p className="text-sm text-gray-500">Your most recent login time</p>
                </div>
                <span className="text-sm text-gray-900">
                  {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
